import { observer } from "mobx-react-lite";
import { Divider, Flex, Form, Typography } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useStores } from "stores";
import { COMPANY_ROLE_ENUM, CompanyEntity, REPORT_TYPE_ENUM } from "interfaces";
import { CompanySelect, WhiteBoxBlock } from "ui";
import { ReportCard } from "components";
import { ENDPOINTS } from "services";
import { useState } from "react";
import { ADDITIONAL_FEES, CUSTOM_LP_REPORT, DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";

export const ReportsPage = observer(() => {
  const { user } = useStores();

  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(null);
  const [form] = Form.useForm();

  const {
    isRiskManager,
    isJuniorLP,
    isSeniorLP,
    isLPRiskManager,
    isUserAdmin,
    isSeniorOperator,
    isJuniorOperator,
    isCustomer,
    isOperator,
    isProvider,
  } = user.roles;

  return (
    <Flex vertical>
      <Typography.Title>Отчеты</Typography.Title>
      <WhiteBoxBlock>
        <Flex vertical gap="large">
          {(isRiskManager || isSeniorOperator) && (
            <>
              <ReportCard
                title="Реестр операций с цифровой валютой"
                datePickerType="single"
                reportType={REPORT_TYPE_ENUM.TX}
                key={REPORT_TYPE_ENUM.TX}
              />
              <Divider />
              <ReportCard
                title="Реестр заявок за период"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.REQUESTS}
                key={REPORT_TYPE_ENUM.REQUESTS}
              />
              <Divider />
              <ReportCard
                title="Адреса-идентификаторы за период"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.WALLETS}
                key={REPORT_TYPE_ENUM.WALLETS}
              />
              <Divider />
            </>
          )}
          {(isUserAdmin || isRiskManager || isSeniorOperator) && (
            <>
              <ReportCard
                title="Реестр клиентов"
                datePickerType="none"
                reportType={REPORT_TYPE_ENUM.CLIENTS}
                key={REPORT_TYPE_ENUM.CLIENTS}
              />
              <Divider />
              <ReportCard
                title="Реестр клиентов агрегированный"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.AGGREGATED}
                key={REPORT_TYPE_ENUM.AGGREGATED}
              />
              <Divider />
            </>
          )}
          {isSeniorOperator && (
            <>
              <ReportCard
                title="Справка о пополнениях на БЧ комиссию"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.FEE_CUR_ALL}
                key={REPORT_TYPE_ENUM.FEE_CUR_ALL}
              />
              <Divider />
            </>
          )}
          {(isJuniorLP || isSeniorLP || isLPRiskManager) && (
            <>
              <ReportCard
                title="Отчет о пополнениях на комиссию БЧ"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.FEE_CUR}
                key={REPORT_TYPE_ENUM.FEE_CUR}
                additionalParams={{
                  lpCompanyRegNumber: user.userInfo!.company_reg_num,
                }}
              />
            </>
          )}
          {isCustomer && (
            <>
              <ReportCard
                title="Отчет о заявках"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.REQUESTS}
                key={uuidv4()}
                customEndpoint={ENDPOINTS.requestReport(user.userInfo!.company_id)}
              />
            </>
          )}
          {!DIRECT_PAYMENT_CUSTOMER_LP && (
            <>
              <ReportCard
                title="Отчет о движении по номинальному счету"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.REQUESTS}
                additionalParams={{
                  companyId: selectedCompany?.id,
                }}
                key={uuidv4()}
                customEndpoint={ENDPOINTS.generateReport("rub-account")}
                reactComponent={
                  isOperator && (
                    <Form form={form} name="addCompany">
                      <Flex style={{ width: "500px", marginTop: "15px" }}>
                        <CompanySelect
                          inputProps={{
                            labelInValue: true,
                            onClear: () => {
                              setSelectedCompany(null);
                            },
                            allowClear: true,
                          }}
                          name="id"
                          onDataReceived={(data) => setSelectedCompany(data)}
                          isRulesDisabled
                        />
                      </Flex>
                    </Form>
                  )
                }
              />
            </>
          )}

          {(isOperator || isProvider) && CUSTOM_LP_REPORT && (
            <>
              <ReportCard
                title="Отчет об остатках и совершенных операциях"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.REMAINING_AND_OPERATIONS}
                additionalParams={{
                  companyId: selectedCompany?.id,
                }}
                key={uuidv4()}
                companySelect={isOperator}
                selectedCompany={selectedCompany}
                reactComponent={
                  isOperator && (
                    <Form form={form} name="addCompany_lp">
                      <Flex style={{ width: "500px", marginTop: "15px" }}>
                        <CompanySelect
                          queryParams={{
                            roles: [COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER],
                          }}
                          inputProps={{
                            labelInValue: true,
                            onClear: () => {
                              setSelectedCompany(null);
                            },
                            allowClear: true,
                          }}
                          name="id_lp"
                          onDataReceived={(data) => setSelectedCompany(data)}
                          isRulesDisabled
                        />
                      </Flex>
                    </Form>
                  )
                }
              />
            </>
          )}

          {ADDITIONAL_FEES && (isSeniorOperator || isJuniorOperator) && (
            <>
              <ReportCard
                title="Отчет по комиссиям заявок"
                datePickerType="range"
                reportType={REPORT_TYPE_ENUM.REQUEST_FEES}
                key={uuidv4()}
                customEndpoint={ENDPOINTS.generateReport("request-fees")}
              />
            </>
          )}
        </Flex>
      </WhiteBoxBlock>
    </Flex>
  );
});
